<template>
  <div class="tc-admin-shell">
    <AppHeader>
      <button
        type="button"
        class="btn btn-outline-secondary btn-sm"
        @click="router.replace('/home')"
      >Back to app</button>
    </AppHeader>

    <div class="tc-admin-layout">
      <!-- Left nav -->
      <nav class="tc-admin-nav" aria-label="Admin sections">
        <div class="tc-admin-nav-group">
          <div class="tc-admin-nav-group-label">Management</div>
          <button
            v-if="isSuperAdmin"
            type="button"
            class="tc-admin-nav-item"
            :class="{ 'tc-admin-nav-item--active': activeSection === 'tenants' }"
            data-testid="admin-nav-tenants"
            @click="activeSection = 'tenants'"
          >Tenants</button>
          <button
            v-if="isSuperAdmin"
            type="button"
            class="tc-admin-nav-item"
            :class="{ 'tc-admin-nav-item--active': activeSection === 'super-admins' }"
            data-testid="admin-nav-super-admins"
            @click="activeSection = 'super-admins'"
          >Super-admins</button>
          <button
            v-if="isTenantAdmin"
            type="button"
            class="tc-admin-nav-item"
            :class="{ 'tc-admin-nav-item--active': activeSection === 'your-admins' }"
            data-testid="admin-nav-your-admins"
            @click="activeSection = 'your-admins'"
          >Your admins</button>
          <button
            type="button"
            class="tc-admin-nav-item"
            :class="{ 'tc-admin-nav-item--active': activeSection === 'sources' }"
            data-testid="admin-nav-sources"
            @click="openSources"
          >Sources</button>
          <button
            type="button"
            class="tc-admin-nav-item"
            data-testid="admin-nav-onboarding"
            @click="router.push('/admin/onboarding')"
          >Onboarding</button>
        </div>
      </nav>

      <!-- Main content -->
      <main class="tc-admin-main tc-admin-main--wide">
        <!-- Initial load -->
        <div v-if="loading" class="tc-admin-loading">
          <span class="tc-admin-spinner" aria-hidden="true"></span>
          Loading…
        </div>

        <div v-else-if="loadError" class="tc-admin-flash tc-admin-flash--error" role="alert">
          {{ loadError }}
        </div>

        <!-- Flash -->
        <div v-if="flash" class="tc-admin-flash" :class="`tc-admin-flash--${flash.type}`" role="status">
          {{ flash.message }}
        </div>

        <!-- ── Tenants ── -->
        <template v-if="!loading && activeSection === 'tenants'">
          <div class="tc-admin-section-header">
            <div class="tc-admin-section-header-row">
              <div>
                <h1 class="tc-admin-section-title">Tenants</h1>
                <p class="tc-admin-section-sub">
                  Each customer's VP connection and Microsoft Graph admin-consent state.
                </p>
              </div>
              <button
                type="button"
                class="btn btn-primary btn-sm"
                data-testid="admin-add-tenant"
                @click="openCreateTenant"
              >Add tenant</button>
            </div>
          </div>

          <div v-if="tenants.length === 0" class="tc-admin-card">
            <div class="tc-admin-empty">
              <span class="tc-admin-empty-headline">No tenants yet</span>
              <span class="tc-admin-empty-hint">Click <strong>Add tenant</strong> to onboard one.</span>
            </div>
          </div>

          <div v-else class="tc-admin-card" data-testid="admin-tenants-table">
            <div class="tc-admin-card-header">
              <span class="tc-admin-card-header-label">All tenants</span>
              <span class="tc-admin-card-count">{{ tenants.length }}</span>
            </div>
            <div class="tc-admin-row-list">
              <div
                v-for="t in tenants"
                :key="t.id"
                class="tc-admin-row"
                :data-testid="`admin-tenant-row-${t.id}`"
              >
                <div class="tc-admin-row-main">
                  <span class="tc-admin-status-dot" :class="`tc-admin-status-dot--${t.status}`" :title="t.status"></span>
                  <div class="tc-admin-row-body">
                    <div class="tc-admin-row-title">{{ t.displayName || t.id }}</div>
                    <div class="tc-admin-row-meta">
                      <span class="tc-admin-mono">{{ t.vpHost }}</span>
                      <span class="tc-admin-row-sep">·</span>
                      <span class="tc-admin-mono">{{ t.vpDatabase }}</span>
                    </div>
                  </div>
                  <div class="tc-admin-row-pills">
                    <span
                      class="tc-admin-pill"
                      :class="t.msGraphConsented ? 'tc-admin-pill--consented' : 'tc-admin-pill--pending'"
                    >{{ t.msGraphConsented ? 'MSGraph OK' : 'consent pending' }}</span>
                  </div>
                </div>
                <div class="tc-admin-row-actions">
                  <button
                    type="button"
                    class="btn btn-outline-secondary btn-sm"
                    :data-testid="`admin-edit-tenant-${t.id}`"
                    @click="openEditTenant(t.id)"
                  >Edit</button>
                  <button
                    type="button"
                    class="btn btn-outline-secondary btn-sm"
                    :data-testid="`admin-consent-${t.id}`"
                    @click="generateConsent(t.id)"
                  >Consent URL</button>
                </div>
              </div>
            </div>
          </div>
        </template>

        <!-- ── Super-admins ── -->
        <template v-if="!loading && activeSection === 'super-admins'">
          <div class="tc-admin-section-header">
            <div class="tc-admin-section-header-row">
              <div>
                <h1 class="tc-admin-section-title">Super-admins</h1>
                <p class="tc-admin-section-sub">
                  FSP staff authorised to administer all tenants.
                  You cannot remove yourself; ask another super-admin.
                </p>
              </div>
              <button
                type="button"
                class="btn btn-primary btn-sm"
                data-testid="admin-add-super-admin"
                @click="superAdminForm.open = true"
              >Add super-admin</button>
            </div>
          </div>

          <div v-if="superAdmins.length === 0" class="tc-admin-card">
            <div class="tc-admin-empty">
              <span class="tc-admin-empty-headline">No super-admins yet</span>
              <span class="tc-admin-empty-hint">Add an FSP staff member above to grant global admin access.</span>
            </div>
          </div>

          <div v-else class="tc-admin-card" data-testid="admin-super-admins-table">
            <div class="tc-admin-card-header">
              <span class="tc-admin-card-header-label">All super-admins</span>
              <span class="tc-admin-card-count">{{ superAdmins.length }}</span>
            </div>
            <div class="tc-admin-row-list">
              <div
                v-for="s in superAdmins"
                :key="`${s.vpTenantId}/${s.vpUserId}`"
                class="tc-admin-row"
                :data-testid="`admin-super-admin-row-${s.vpTenantId}-${s.vpUserId}`"
              >
                <div class="tc-admin-row-main">
                  <span class="tc-admin-status-dot tc-admin-status-dot--active"></span>
                  <div class="tc-admin-row-body">
                    <div class="tc-admin-row-title tc-admin-mono">{{ s.vpUserId }}</div>
                    <div class="tc-admin-row-meta">
                      <span class="tc-admin-mono">{{ s.vpTenantId }}</span>
                      <span class="tc-admin-row-sep">·</span>
                      Added {{ formatDate(s.createdUtc) }} by <span class="tc-admin-mono">{{ s.createdBy }}</span>
                    </div>
                  </div>
                </div>
                <div class="tc-admin-row-actions">
                  <button
                    type="button"
                    class="btn btn-outline-danger btn-sm"
                    :disabled="isSelf(s)"
                    :title="isSelf(s) ? 'You cannot remove yourself.' : 'Remove this super-admin'"
                    :data-testid="`admin-remove-super-admin-${s.vpTenantId}-${s.vpUserId}`"
                    @click="promptRemoveSuperAdmin(s)"
                  >Remove</button>
                </div>
              </div>
            </div>
          </div>
        </template>

        <!-- ── Your admins (tenant-scoped) ── -->
        <template v-if="!loading && activeSection === 'your-admins'">
          <div class="tc-admin-section-header">
            <div class="tc-admin-section-header-row">
              <div>
                <h1 class="tc-admin-section-title">Your admins</h1>
                <p class="tc-admin-section-sub">
                  People who can run onboarding and tenant setup for {{ me?.vpTenantId }}.
                  You cannot remove yourself; ask another admin.
                </p>
              </div>
              <button
                type="button"
                class="btn btn-primary btn-sm"
                data-testid="admin-add-tenant-admin"
                @click="tenantAdminForm.open = true"
              >Add admin</button>
            </div>
          </div>

          <div v-if="tenantAdmins.length === 0" class="tc-admin-card">
            <div class="tc-admin-empty">
              <span class="tc-admin-empty-headline">No admins yet</span>
              <span class="tc-admin-empty-hint">Add a colleague above to share setup access.</span>
            </div>
          </div>

          <div v-else class="tc-admin-card" data-testid="admin-tenant-admins-table">
            <div class="tc-admin-card-header">
              <span class="tc-admin-card-header-label">All admins</span>
              <span class="tc-admin-card-count">{{ tenantAdmins.length }}</span>
            </div>
            <div class="tc-admin-row-list">
              <div
                v-for="a in tenantAdmins"
                :key="a.vpUserId"
                class="tc-admin-row"
                :data-testid="`admin-tenant-admin-row-${a.vpUserId}`"
              >
                <div class="tc-admin-row-main">
                  <span class="tc-admin-status-dot tc-admin-status-dot--active"></span>
                  <div class="tc-admin-row-body">
                    <div class="tc-admin-row-title tc-admin-mono">{{ a.vpUserId }}</div>
                    <div class="tc-admin-row-meta">
                      Added {{ formatDate(a.createdUtc) }} by <span class="tc-admin-mono">{{ a.createdBy }}</span>
                    </div>
                  </div>
                </div>
                <div class="tc-admin-row-actions">
                  <button
                    type="button"
                    class="btn btn-outline-danger btn-sm"
                    :disabled="isSelfTenantAdmin(a)"
                    :title="isSelfTenantAdmin(a) ? 'You cannot remove yourself.' : 'Remove this admin'"
                    :data-testid="`admin-remove-tenant-admin-${a.vpUserId}`"
                    @click="promptRemoveTenantAdmin(a)"
                  >Remove</button>
                </div>
              </div>
            </div>
          </div>
        </template>

        <!-- ── Sources (tenant-scoped connections) ── -->
        <template v-if="!loading && activeSection === 'sources'">
          <div class="tc-admin-section-header">
            <h1 class="tc-admin-section-title">Sources</h1>
            <p class="tc-admin-section-sub">
              Connections {{ me?.vpTenantId }} makes once. Each user then connects their own account from Sources.
            </p>
          </div>

          <div class="tc-admin-card" data-testid="admin-sources-table">
            <div class="tc-admin-row-list">
              <div class="tc-admin-row" data-testid="admin-source-row-zoom">
                <div class="tc-admin-row-main">
                  <span
                    class="tc-admin-status-dot"
                    :class="{ 'tc-admin-status-dot--active': zoomTenantConnected }"
                  ></span>
                  <div class="tc-admin-row-body">
                    <div class="tc-admin-row-title">Zoom</div>
                    <div class="tc-admin-row-meta" data-testid="admin-zoom-status">{{ zoomTenantStatus }}</div>
                  </div>
                </div>
                <div class="tc-admin-row-actions">
                  <button
                    v-if="zoomTenantConnected === true"
                    type="button"
                    class="btn btn-outline-danger btn-sm"
                    data-testid="admin-zoom-disconnect"
                    @click="zoomDisconnectConfirmOpen = true"
                  >Disconnect</button>
                  <button
                    v-else-if="zoomTenantConnected === false"
                    type="button"
                    class="btn btn-primary btn-sm"
                    data-testid="admin-zoom-connect"
                    @click="connectZoomTenant"
                  >Connect</button>
                </div>
              </div>
            </div>
          </div>
        </template>
      </main>
    </div>

    <!-- ── Tenant create/edit modal ── -->
    <div v-if="tenantForm.open" class="tc-admin-modal-backdrop" role="dialog" aria-modal="true">
      <div class="tc-admin-modal">
        <header class="tc-admin-modal-header">
          <h2 class="tc-admin-modal-title">
            {{ tenantForm.mode === 'create' ? 'Add tenant' : `Edit tenant: ${tenantForm.id}` }}
          </h2>
          <button
            type="button"
            class="tc-admin-modal-close"
            aria-label="Close"
            @click="closeTenantForm"
          >&times;</button>
        </header>

        <form class="tc-admin-form" @submit.prevent="submitTenantForm">
          <div class="tc-admin-field" v-if="tenantForm.mode === 'create'">
            <label class="tc-admin-label" for="tenant-id">ID</label>
            <input
              id="tenant-id"
              type="text"
              class="form-control"
              placeholder="acme"
              v-model.trim="tenantForm.id"
              autocomplete="off"
              data-testid="admin-tenant-id"
              required
            />
            <div class="tc-admin-field-hint">
              Lowercase alphanumeric + hyphen, 2-30 chars. Immutable after create.
            </div>
          </div>

          <div class="tc-admin-field">
            <label class="tc-admin-label" for="tenant-display">Display name</label>
            <input
              id="tenant-display"
              type="text"
              class="form-control"
              placeholder="Acme Corp"
              v-model.trim="tenantForm.displayName"
              autocomplete="off"
              data-testid="admin-tenant-display"
            />
          </div>

          <div class="tc-admin-field">
            <label class="tc-admin-label" for="tenant-vp-host">VP host URL</label>
            <input
              id="tenant-vp-host"
              type="url"
              class="form-control"
              placeholder="https://acme.dvphosted.com/acme"
              v-model.trim="tenantForm.vpHost"
              autocomplete="off"
              data-testid="admin-tenant-vp-host"
              required
            />
          </div>

          <div class="tc-admin-field">
            <label class="tc-admin-label" for="tenant-vp-database">VP database</label>
            <input
              id="tenant-vp-database"
              type="text"
              class="form-control"
              placeholder="ACME_PROD"
              v-model.trim="tenantForm.vpDatabase"
              autocomplete="off"
              data-testid="admin-tenant-vp-database"
              required
            />
          </div>

          <div class="tc-admin-field">
            <label class="tc-admin-label" for="tenant-vp-client-id">VP OAuth client ID</label>
            <input
              id="tenant-vp-client-id"
              type="text"
              class="form-control"
              v-model.trim="tenantForm.vpClientId"
              autocomplete="off"
              data-testid="admin-tenant-vp-client-id"
              required
            />
          </div>

          <div class="tc-admin-field">
            <label class="tc-admin-label" for="tenant-vp-secret">
              {{ tenantForm.mode === 'create' ? 'VP OAuth client secret' : 'Replace VP OAuth client secret' }}
            </label>
            <input
              id="tenant-vp-secret"
              type="password"
              class="form-control"
              :placeholder="tenantForm.mode === 'edit' && tenantForm.vpClientSecretSet
                ? '(stored — enter new value to replace)'
                : 'Enter VP OAuth client secret'"
              v-model="tenantForm.vpClientSecret"
              autocomplete="new-password"
              data-testid="admin-tenant-vp-secret"
            />
            <div
              v-if="tenantForm.mode === 'edit' && tenantForm.vpClientSecretSet"
              class="tc-admin-field-hint tc-admin-field-hint--set"
            >Secret is set. Leave blank to keep the existing value.</div>
          </div>

          <div class="tc-admin-field" v-if="tenantForm.mode === 'edit'">
            <label class="tc-admin-label" for="tenant-status">Status</label>
            <select
              id="tenant-status"
              class="form-select"
              v-model="tenantForm.status"
              data-testid="admin-tenant-status"
            >
              <option value="active">active</option>
              <option value="disabled">disabled (soft-revoke at sign-in)</option>
            </select>
          </div>

          <div class="tc-admin-field" v-if="tenantForm.mode === 'edit'">
            <label class="tc-admin-label">AI drafting</label>
            <div class="form-check form-switch">
              <input
                id="tenant-agent-enabled"
                class="form-check-input"
                type="checkbox"
                role="switch"
                v-model="tenantForm.agentEnabled"
                data-testid="admin-tenant-agent-enabled"
              />
              <label class="form-check-label" for="tenant-agent-enabled">Enabled</label>
            </div>
            <div class="tc-admin-field-hint">
              Off = deterministic rules only; no calendar content is sent to a model.
            </div>
          </div>

          <div class="tc-admin-form-actions">
            <button
              type="button"
              class="btn btn-outline-secondary"
              :disabled="tenantForm.saving"
              @click="closeTenantForm"
            >Cancel</button>
            <button
              type="submit"
              class="btn btn-primary"
              :disabled="tenantForm.saving"
              data-testid="admin-tenant-submit"
            >
              <span v-if="tenantForm.saving" class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
              {{ tenantForm.saving ? 'Saving…' : tenantForm.mode === 'create' ? 'Create tenant' : 'Save changes' }}
            </button>
          </div>
        </form>

        <!-- Tenant admins — cross-tenant bootstrap (super-admin only; ADR-0040) -->
        <div v-if="tenantForm.mode === 'edit'" class="tc-admin-card mt-3" data-testid="admin-tenant-detail-admins">
          <div class="tc-admin-card-header">
            <span class="tc-admin-card-header-label">Tenant admins</span>
            <span class="tc-admin-card-count">{{ editingTenantAdmins.length }}</span>
          </div>

          <div v-if="editingTenantAdmins.length === 0" class="tc-admin-empty">
            <span class="tc-admin-empty-hint">No admins appointed yet.</span>
          </div>
          <div v-else class="tc-admin-row-list">
            <div
              v-for="a in editingTenantAdmins"
              :key="a.vpUserId"
              class="tc-admin-row"
              :data-testid="`admin-detail-tenant-admin-row-${a.vpUserId}`"
            >
              <div class="tc-admin-row-main">
                <span class="tc-admin-status-dot tc-admin-status-dot--active"></span>
                <div class="tc-admin-row-body">
                  <div class="tc-admin-row-title tc-admin-mono">{{ a.vpUserId }}</div>
                  <div class="tc-admin-row-meta">Added {{ formatDate(a.createdUtc) }}</div>
                </div>
              </div>
              <div class="tc-admin-row-actions">
                <button
                  type="button"
                  class="btn btn-outline-danger btn-sm"
                  :data-testid="`admin-detail-remove-tenant-admin-${a.vpUserId}`"
                  @click="promptRemoveEditingTenantAdmin(a)"
                >Remove</button>
              </div>
            </div>
          </div>

          <div class="tc-admin-field p-3">
            <label class="tc-admin-label" for="appoint-vp-user-id">Appoint first admin</label>
            <div class="d-flex gap-2">
              <input
                id="appoint-vp-user-id"
                type="text"
                class="form-control"
                placeholder="VP employee ID"
                v-model.trim="appointForm.vpUserId"
                autocomplete="off"
                data-testid="admin-appoint-vp-user-id"
              />
              <button
                type="button"
                class="btn btn-outline-primary btn-sm"
                :disabled="appointForm.saving || !appointForm.vpUserId"
                data-testid="admin-appoint-submit"
                @click="submitAppointTenantAdmin"
              >
                <span v-if="appointForm.saving" class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                Appoint
              </button>
            </div>
            <div class="tc-admin-field-hint">
              They become an admin only once they sign into {{ tenantForm.id }}'s Vantagepoint.
              No employee search here — FSP holds no bearer in a customer's VP.
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- ── Super-admin add modal ── -->
    <div v-if="superAdminForm.open" class="tc-admin-modal-backdrop" role="dialog" aria-modal="true">
      <div class="tc-admin-modal">
        <header class="tc-admin-modal-header">
          <h2 class="tc-admin-modal-title">Add super-admin</h2>
          <button
            type="button"
            class="tc-admin-modal-close"
            aria-label="Close"
            @click="closeSuperAdminForm"
          >&times;</button>
        </header>
        <form class="tc-admin-form" @submit.prevent="submitSuperAdminForm">
          <div class="tc-admin-field">
            <EmployeePicker v-model="superAdminForm.employee" />
            <div class="tc-admin-field-hint">
              Type 2+ characters to search by name, code, or email. Returns only active employees.
            </div>
          </div>
          <div class="tc-admin-form-actions">
            <button
              type="button"
              class="btn btn-outline-secondary"
              :disabled="superAdminForm.saving"
              @click="closeSuperAdminForm"
            >Cancel</button>
            <button
              type="submit"
              class="btn btn-primary"
              :disabled="superAdminForm.saving || !superAdminForm.employee"
              data-testid="admin-super-admin-submit"
            >
              <span v-if="superAdminForm.saving" class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
              {{ superAdminForm.saving ? 'Saving…' : 'Add super-admin' }}
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- ── Tenant-admin add modal ── -->
    <div v-if="tenantAdminForm.open" class="tc-admin-modal-backdrop" role="dialog" aria-modal="true">
      <div class="tc-admin-modal">
        <header class="tc-admin-modal-header">
          <h2 class="tc-admin-modal-title">Add admin</h2>
          <button
            type="button"
            class="tc-admin-modal-close"
            aria-label="Close"
            @click="closeTenantAdminForm"
          >&times;</button>
        </header>
        <form class="tc-admin-form" @submit.prevent="submitTenantAdminForm">
          <div class="tc-admin-field">
            <EmployeePicker v-model="tenantAdminForm.employee" />
            <div class="tc-admin-field-hint">
              Type 2+ characters to search by name, code, or email. Returns only active employees.
            </div>
          </div>
          <div class="tc-admin-form-actions">
            <button
              type="button"
              class="btn btn-outline-secondary"
              :disabled="tenantAdminForm.saving"
              @click="closeTenantAdminForm"
            >Cancel</button>
            <button
              type="submit"
              class="btn btn-primary"
              :disabled="tenantAdminForm.saving || !tenantAdminForm.employee"
              data-testid="admin-tenant-admin-submit"
            >
              <span v-if="tenantAdminForm.saving" class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
              {{ tenantAdminForm.saving ? 'Saving…' : 'Add admin' }}
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- ── Admin-consent URL modal ── -->
    <div v-if="consentModal" class="tc-admin-modal-backdrop" role="dialog" aria-modal="true">
      <div class="tc-admin-modal">
        <header class="tc-admin-modal-header">
          <h2 class="tc-admin-modal-title">Admin-consent URL — {{ consentModal.tenantId }}</h2>
          <button
            type="button"
            class="tc-admin-modal-close"
            aria-label="Close"
            @click="consentModal = null"
          >&times;</button>
        </header>
        <p class="tc-admin-section-sub">
          Send this URL to the customer's Entra admin. They open it, sign in, and
          grant Microsoft Graph admin-consent for their organisation. After they
          click "Accept", we record their AAD tenant id on this tenant row.
          <br><br>
          The URL expires at {{ formatDate(consentModal.expiresAt) }}.
        </p>
        <div class="tc-admin-consent-url" data-testid="admin-consent-url">{{ consentModal.url }}</div>
        <div class="tc-admin-form-actions">
          <button
            type="button"
            class="btn btn-outline-secondary"
            @click="copyConsentUrl"
          >{{ consentCopied ? 'Copied' : 'Copy URL' }}</button>
          <button
            type="button"
            class="btn btn-primary"
            @click="consentModal = null"
          >Close</button>
        </div>
      </div>
    </div>

    <!-- ── Remove super-admin confirmation ── -->
    <ConfirmDialog
      :open="removeConfirm.open"
      title="Remove super-admin"
      :message="removeConfirm.message"
      confirm-label="Remove"
      :destructive="true"
      :on-confirm="doRemoveSuperAdmin"
      @close="removeConfirm.open = false"
    />

    <!-- ── Remove tenant-admin confirmation ── -->
    <ConfirmDialog
      :open="removeTenantAdminConfirm.open"
      title="Remove admin"
      :message="removeTenantAdminConfirm.message"
      confirm-label="Remove"
      :destructive="true"
      :on-confirm="doRemoveTenantAdmin"
      @close="removeTenantAdminConfirm.open = false"
    />

    <!-- ── Disconnect the tenant's Zoom confirmation ── -->
    <ConfirmDialog
      :open="zoomDisconnectConfirmOpen"
      title="Disconnect Zoom"
      :message="`Disconnect Zoom for ${me?.vpTenantId}? Nobody's Zoom meetings are read until an admin connects it again.`"
      confirm-label="Disconnect"
      :destructive="true"
      :on-confirm="doDisconnectZoomTenant"
      @close="zoomDisconnectConfirmOpen = false"
    />

    <!-- ── Remove tenant-admin confirmation (tenant detail) ── -->
    <ConfirmDialog
      :open="removeEditingConfirm.open"
      title="Remove admin"
      :message="removeEditingConfirm.message"
      confirm-label="Remove"
      :destructive="true"
      :on-confirm="doRemoveEditingTenantAdmin"
      @close="removeEditingConfirm.open = false"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, computed, onMounted, onUnmounted } from 'vue';
import { useRouter } from 'vue-router';
import AppHeader from '../components/AppHeader.vue';
import ConfirmDialog from '../components/ConfirmDialog.vue';
import {
  listTenants, getTenant, createTenant, updateTenant, replaceVpClientSecret,
  generateAdminConsentUrl, listSuperAdmins, addSuperAdmin, removeSuperAdmin,
  listTenantAdmins, addTenantAdmin, removeTenantAdmin,
  getZoomTenantConnection, disconnectZoomTenant,
  type TenantSummary, type TenantDetail, type SuperAdminEntry, type TenantAdminEntry, type Employee,
} from '../api/admin';
import { getAuthMe, ApiClientError } from '../api/client';
import EmployeePicker from '../components/EmployeePicker.vue';

const router = useRouter();

type Section = 'tenants' | 'super-admins' | 'your-admins' | 'sources';
const activeSection = ref<Section>('tenants');

const loading = ref(true);
const loadError = ref<string | null>(null);
const flash = ref<{ type: 'success' | 'error'; message: string } | null>(null);

const tenants = ref<TenantSummary[]>([]);
const superAdmins = ref<SuperAdminEntry[]>([]);
const tenantAdmins = ref<TenantAdminEntry[]>([]);

const me = ref<{
  vpTenantId: string;
  vpEmployeeId: string;
  superAdmin: boolean;
  tenantAdmin: boolean;
} | null>(null);
const isSuperAdmin = computed(() => me.value?.superAdmin ?? false);
const isTenantAdmin = computed(() => me.value?.tenantAdmin ?? false);

interface TenantFormState {
  open: boolean;
  mode: 'create' | 'edit';
  id: string;
  displayName: string;
  vpHost: string;
  vpDatabase: string;
  vpClientId: string;
  vpClientSecret: string;
  vpClientSecretSet: boolean;
  status: 'active' | 'disabled';
  agentEnabled: boolean;
  saving: boolean;
}
const tenantForm = reactive<TenantFormState>({
  open: false, mode: 'create',
  id: '', displayName: '', vpHost: '', vpDatabase: '', vpClientId: '',
  vpClientSecret: '', vpClientSecretSet: false, status: 'active',
  agentEnabled: true,
  saving: false,
});

const superAdminForm = reactive<{
  open: boolean;
  employee: Employee | null;
  saving: boolean;
}>({
  open: false,
  employee: null,
  saving: false,
});

// Own-tenant admin add (self-service — mirrors superAdminForm).
const tenantAdminForm = reactive<{
  open: boolean;
  employee: Employee | null;
  saving: boolean;
}>({
  open: false,
  employee: null,
  saving: false,
});

// Cross-tenant bootstrap seed, scoped to whichever tenant is open in the
// edit modal (super-admin only — see ADR-0040 "Bootstrap and management").
const editingTenantAdmins = ref<TenantAdminEntry[]>([]);
const appointForm = reactive<{ vpUserId: string; saving: boolean }>({
  vpUserId: '',
  saving: false,
});

const consentModal = ref<{ tenantId: string; url: string; expiresAt: string } | null>(null);
const consentCopied = ref(false);

const removeConfirm = reactive<{
  open: boolean;
  message: string;
  target: SuperAdminEntry | null;
}>({
  open: false,
  message: '',
  target: null,
});

const removeTenantAdminConfirm = reactive<{
  open: boolean;
  message: string;
  target: TenantAdminEntry | null;
}>({
  open: false,
  message: '',
  target: null,
});

// Super-admin's remove from the tenant-detail card. Separate from the
// your-admins confirm above because it refreshes the modal's local list,
// not reloadAll().
const removeEditingConfirm = reactive<{
  open: boolean;
  message: string;
  target: TenantAdminEntry | null;
}>({
  open: false,
  message: '',
  target: null,
});

onMounted(async () => {
  try {
    const meResp = await getAuthMe();
    me.value = {
      vpTenantId: meResp.vpTenantId,
      vpEmployeeId: meResp.vpEmployeeId,
      superAdmin: meResp.superAdmin,
      tenantAdmin: meResp.tenantAdmin,
    };
    activeSection.value = meResp.superAdmin ? 'tenants' : 'your-admins';
    await reloadAll();
  } catch (e) {
    if (e instanceof ApiClientError && e.status === 403) {
      await router.replace('/home');
      return;
    }
    loadError.value = e instanceof Error ? e.message : 'Failed to load admin data.';
  } finally {
    loading.value = false;
  }
});

// Data fetched is role-gated: tenant CRUD + super-admin roster stay behind
// the SuperAdmin policy server-side, so a tenant_admin-only caller must never
// call them (they would 403). Your-own-tenant admins are visible to both.
async function reloadAll(): Promise<void> {
  const tasks: Promise<void>[] = [];
  if (me.value?.superAdmin) {
    tasks.push(listTenants().then(t => { tenants.value = t; }));
    tasks.push(listSuperAdmins().then(s => { superAdmins.value = s; }));
  }
  if (me.value?.tenantAdmin) {
    tasks.push(listTenantAdmins(me.value.vpTenantId).then(a => { tenantAdmins.value = a; }));
  }
  await Promise.all(tasks);
}

// ── Tenants ───────────────────────────────────────────────────────────────

function openCreateTenant(): void {
  Object.assign(tenantForm, {
    open: true, mode: 'create',
    id: '', displayName: '', vpHost: '', vpDatabase: '', vpClientId: '',
    vpClientSecret: '', vpClientSecretSet: false, status: 'active',
    agentEnabled: true,
    saving: false,
  });
}

async function openEditTenant(id: string): Promise<void> {
  try {
    const [detail, admins] = await Promise.all([getTenant(id), listTenantAdmins(id)]);
    fillTenantFormFromDetail(detail);
    editingTenantAdmins.value = admins;
    appointForm.vpUserId = '';
    appointForm.saving = false;
    tenantForm.open = true;
  } catch (e) {
    showFlash('error', e instanceof Error ? e.message : 'Failed to load tenant.');
  }
}

function fillTenantFormFromDetail(detail: TenantDetail): void {
  tenantForm.mode = 'edit';
  tenantForm.id = detail.id;
  tenantForm.displayName = detail.displayName;
  tenantForm.vpHost = detail.vpHost;
  tenantForm.vpDatabase = detail.vpDatabase;
  tenantForm.vpClientId = detail.vpClientId;
  tenantForm.vpClientSecret = '';
  tenantForm.vpClientSecretSet = detail.vpClientSecretSet;
  tenantForm.status = (detail.status === 'disabled' ? 'disabled' : 'active');
  tenantForm.agentEnabled = detail.agentEnabled;
  tenantForm.saving = false;
}

function closeTenantForm(): void {
  tenantForm.open = false;
  tenantForm.saving = false;
  editingTenantAdmins.value = [];
}

async function submitAppointTenantAdmin(): Promise<void> {
  const vpUserId = appointForm.vpUserId.trim();
  if (!vpUserId) return;
  appointForm.saving = true;
  try {
    await addTenantAdmin(tenantForm.id, { vpUserId });
    editingTenantAdmins.value = await listTenantAdmins(tenantForm.id);
    appointForm.vpUserId = '';
    showFlash('success',
      `${vpUserId} appointed. They become an admin once they sign into ${tenantForm.id}'s Vantagepoint.`);
  } catch (e) {
    showFlash('error', e instanceof Error ? e.message : 'Appoint failed.');
  } finally {
    appointForm.saving = false;
  }
}

function promptRemoveEditingTenantAdmin(a: TenantAdminEntry): void {
  removeEditingConfirm.target = a;
  removeEditingConfirm.message = `Remove ${a.vpUserId} as an admin? This cannot be undone.`;
  removeEditingConfirm.open = true;
}

async function doRemoveEditingTenantAdmin(): Promise<void> {
  const a = removeEditingConfirm.target;
  if (!a) return;
  try {
    await removeTenantAdmin(a.vpTenantId, a.vpUserId);
    editingTenantAdmins.value = editingTenantAdmins.value.filter(x => x.vpUserId !== a.vpUserId);
  } catch (e) {
    if (e instanceof ApiClientError && e.code === 'cannot_remove_self') {
      showFlash('error', "You can't remove yourself.");
    } else if (e instanceof ApiClientError && e.code === 'tenant_admin_not_found') {
      editingTenantAdmins.value = editingTenantAdmins.value.filter(x => x.vpUserId !== a.vpUserId);
    } else {
      showFlash('error', e instanceof Error ? e.message : 'Remove failed.');
    }
  }
}

async function submitTenantForm(): Promise<void> {
  tenantForm.saving = true;
  try {
    if (tenantForm.mode === 'create') {
      await createTenant({
        id: tenantForm.id,
        displayName: tenantForm.displayName || undefined,
        vpHost: tenantForm.vpHost,
        vpDatabase: tenantForm.vpDatabase,
        vpClientId: tenantForm.vpClientId,
        vpClientSecret: tenantForm.vpClientSecret || undefined,
      });
      showFlash('success', `Tenant '${tenantForm.id}' created.`);
    } else {
      await updateTenant(tenantForm.id, {
        displayName: tenantForm.displayName || undefined,
        vpHost: tenantForm.vpHost || undefined,
        vpDatabase: tenantForm.vpDatabase || undefined,
        vpClientId: tenantForm.vpClientId || undefined,
        status: tenantForm.status,
        agentEnabled: tenantForm.agentEnabled,
      });
      if (tenantForm.vpClientSecret) {
        await replaceVpClientSecret(tenantForm.id, tenantForm.vpClientSecret);
      }
      showFlash('success', `Tenant '${tenantForm.id}' updated.`);
    }
    closeTenantForm();
    await reloadAll();
  } catch (e) {
    const msg = e instanceof Error ? e.message : 'Save failed.';
    showFlash('error', msg);
    tenantForm.saving = false;
  }
}

async function generateConsent(tenantId: string): Promise<void> {
  try {
    const resp = await generateAdminConsentUrl(tenantId);
    consentModal.value = { tenantId, url: resp.url, expiresAt: resp.expiresAt };
    consentCopied.value = false;
  } catch (e) {
    showFlash('error', e instanceof Error ? e.message : 'Failed to generate admin-consent URL.');
  }
}

async function copyConsentUrl(): Promise<void> {
  if (!consentModal.value) return;
  try {
    await navigator.clipboard.writeText(consentModal.value.url);
    consentCopied.value = true;
  } catch {
    consentCopied.value = false;
  }
}

// ── Super-admins ──────────────────────────────────────────────────────────

function closeSuperAdminForm(): void {
  superAdminForm.open = false;
  superAdminForm.employee = null;
  superAdminForm.saving = false;
}

async function submitSuperAdminForm(): Promise<void> {
  if (!superAdminForm.employee) return;
  const picked = superAdminForm.employee;
  superAdminForm.saving = true;
  try {
    await addSuperAdmin({ vpUserId: picked.employee });
    showFlash('success',
      `Super-admin (${me.value?.vpTenantId ?? ''}, ${picked.employee}) added.`);
    closeSuperAdminForm();
    await reloadAll();
  } catch (e) {
    showFlash('error', e instanceof Error ? e.message : 'Add failed.');
    superAdminForm.saving = false;
  }
}

function promptRemoveSuperAdmin(s: SuperAdminEntry): void {
  if (isSelf(s)) return;
  removeConfirm.target = s;
  removeConfirm.message = `Remove super-admin ${s.vpTenantId}/${s.vpUserId}? This cannot be undone.`;
  removeConfirm.open = true;
}

async function doRemoveSuperAdmin(): Promise<void> {
  const s = removeConfirm.target;
  if (!s) return;
  await removeSuperAdmin(s.vpTenantId, s.vpUserId);
  showFlash('success', `Super-admin (${s.vpTenantId}, ${s.vpUserId}) removed.`);
  await reloadAll();
}

function isSelf(s: SuperAdminEntry): boolean {
  return !!me.value
    && s.vpTenantId.toLowerCase() === me.value.vpTenantId.toLowerCase()
    && s.vpUserId.toLowerCase() === me.value.vpEmployeeId.toLowerCase();
}

// ── Your admins (own tenant) ─────────────────────────────────────────────

function closeTenantAdminForm(): void {
  tenantAdminForm.open = false;
  tenantAdminForm.employee = null;
  tenantAdminForm.saving = false;
}

async function submitTenantAdminForm(): Promise<void> {
  if (!tenantAdminForm.employee || !me.value) return;
  const picked = tenantAdminForm.employee;
  tenantAdminForm.saving = true;
  try {
    await addTenantAdmin(me.value.vpTenantId, { vpUserId: picked.employee });
    showFlash('success', `${picked.employee} added as an admin.`);
    closeTenantAdminForm();
    await reloadAll();
  } catch (e) {
    showFlash('error', e instanceof Error ? e.message : 'Add failed.');
    tenantAdminForm.saving = false;
  }
}

function promptRemoveTenantAdmin(a: TenantAdminEntry): void {
  if (isSelfTenantAdmin(a)) return;
  removeTenantAdminConfirm.target = a;
  removeTenantAdminConfirm.message = `Remove ${a.vpUserId} as an admin? This cannot be undone.`;
  removeTenantAdminConfirm.open = true;
}

async function doRemoveTenantAdmin(): Promise<void> {
  const a = removeTenantAdminConfirm.target;
  if (!a) return;
  try {
    await removeTenantAdmin(a.vpTenantId, a.vpUserId);
    showFlash('success', `${a.vpUserId} removed as admin.`);
  } catch (e) {
    if (e instanceof ApiClientError && e.code === 'cannot_remove_self') {
      showFlash('error', "You can't remove yourself.");
    } else if (!(e instanceof ApiClientError && e.code === 'tenant_admin_not_found')) {
      showFlash('error', e instanceof Error ? e.message : 'Remove failed.');
    }
  }
  await reloadAll();
}

function isSelfTenantAdmin(a: TenantAdminEntry): boolean {
  return !!me.value && a.vpUserId.toLowerCase() === me.value.vpEmployeeId.toLowerCase();
}

// ── Sources (own tenant) ─────────────────────────────────────────────────

// null until the first load, so neither button shows before the state is known.
const zoomTenantConnected = ref<boolean | null>(null);
const zoomDisconnectConfirmOpen = ref(false);

const zoomTenantStatus = computed(() => {
  if (zoomTenantConnected.value === null) return 'Loading…';
  return zoomTenantConnected.value ? 'Connected' : 'Not connected. A Zoom admin connects it once.';
});

async function openSources(): Promise<void> {
  activeSection.value = 'sources';
  await loadZoomTenantConnection();
}

async function loadZoomTenantConnection(): Promise<void> {
  try {
    zoomTenantConnected.value = (await getZoomTenantConnection()).connected;
  } catch (e) {
    showFlash('error', e instanceof Error ? e.message : 'Failed to load the Zoom connection.');
  }
}

// MUST call window.open synchronously from the click handler; any await first
// triggers popup-blocker heuristics (ADR-0029 §6).
function connectZoomTenant(): void {
  window.open('/api/zoom/tenant/start?return=popup', '_blank', 'width=480,height=720,noopener=no');
}

async function doDisconnectZoomTenant(): Promise<void> {
  try {
    await disconnectZoomTenant();
    showFlash('success', 'Zoom disconnected.');
  } catch (e) {
    showFlash('error', e instanceof Error ? e.message : 'Disconnect failed.');
  }
  await loadZoomTenantConnection();
}

// The OAuth popup reports back with a same-origin postMessage (ADR-0029 §6).
function onZoomTenantLinked(event: MessageEvent): void {
  if (event.origin !== window.location.origin) return;
  if ((event.data as { type?: string } | null)?.type === 'zoom_tenant_linked') void loadZoomTenantConnection();
}

onMounted(() => window.addEventListener('message', onZoomTenantLinked));
onUnmounted(() => window.removeEventListener('message', onZoomTenantLinked));

// ── Helpers ───────────────────────────────────────────────────────────────

function showFlash(type: 'success' | 'error', message: string): void {
  flash.value = { type, message };
  if (type === 'success') {
    window.setTimeout(() => {
      if (flash.value?.message === message) flash.value = null;
    }, 4000);
  }
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}
</script>
